import { getExplicitFitnessMovement } from "@/lib/fitnessExerciseIdentity";
import React, { useState } from "react";
import { ChevronRight, Lightbulb, Loader2, Square, Volume2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { getExerciseGuidance } from "@/lib/fitnessExerciseGuidance";
import { useExerciseCoachSpeech } from "@/hooks/useExerciseCoachSpeech";

interface ExerciseCoachProps {
  exerciseName: string;
  exerciseId?: string;
  speechScopeId?: string;
  currentSet: number;
  paused?: boolean;
}

export default function ExerciseCoach({ exerciseName, exerciseId, speechScopeId, currentSet, paused = false }: ExerciseCoachProps) {
  const { lang } = useLanguage();
  const explicit = getExplicitFitnessMovement(exerciseId || "");
  const guidance = explicit ? getExerciseGuidance(explicit.canonicalName, lang)
    : getExerciseGuidance(exerciseId || "", lang) ?? getExerciseGuidance(exerciseName, lang);
  if (!guidance?.cues.length) return null;
  const identity = JSON.stringify([speechScopeId, exerciseId, exerciseName, currentSet, lang]);
  return <SetCue key={identity} identity={identity}
    cues={guidance.cues} currentSet={currentSet} paused={paused} />;
}

function SetCue({ cues, currentSet, paused, identity }: { cues: string[]; currentSet: number; paused: boolean; identity: string }) {
  const { t, tf, lang } = useLanguage();
  const initial = Math.max(0, currentSet - 1) % cues.length;
  const [offset, setOffset] = useState(0);
  const index = (initial + offset) % cues.length;
  const speech = useExerciseCoachSpeech({ text: cues[index], identity: identity + ":" + index, language: lang, paused });
  return (
    <aside aria-label={t("fitness.coach.title")} className="rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3">
      <audio ref={speech.attachAudio} playsInline preload="none" className="hidden" aria-hidden="true" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Lightbulb size={17} aria-hidden="true" className="shrink-0 text-primary" />
          {t("fitness.coach.title")}
        </h3>
        {cues.length > 1 && <button type="button" disabled={paused}
          onClick={() => { speech.stop(); setOffset(value => value + 1); }}
          aria-label={t("fitness.coach.next")}
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-xl px-2 text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
          <span>{tf("fitness.coach.tipOf", { current: String(index + 1), total: String(cues.length) })}</span>
          <ChevronRight size={17} aria-hidden="true" />
        </button>}
      </div>
      <p aria-live="polite" aria-atomic="true" className="mt-1 text-sm leading-relaxed text-foreground">{cues[index]}</p>
      <button type="button" disabled={paused || speech.blocked}
        onClick={() => void speech.listen()}
        className="mt-2 flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
        {speech.busy ? <Square size={16} aria-hidden="true" /> : <Volume2 size={17} aria-hidden="true" />}
        {t(speech.busy ? "fitness.coach.stop" : "fitness.coach.listen")}
        {speech.loading && <Loader2 size={14} className="animate-spin" aria-label={t("fitness.coach.loading")} />}
      </button>
      {(speech.blocked || speech.message) && <p role="status" className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {t("fitness.coach." + (speech.blocked ? "audioUnavailable" : speech.message))}
      </p>}
    </aside>
  );
}
